import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  alpacaService,
  Account,
  Position,
  ScanResult,
  PositionTarget,
  PositionIntelEntry,
} from '../../services/AlpacaService';
import { useAccountMode } from '../../context/AccountModeContext';
import { useAuth } from '../../context/AuthContext';
import { useDemo } from '../../context/DemoContext';
import { DEMO_ACCOUNT, DEMO_POSITIONS } from '../../services/demoData';
import MoneyRain from '../../components/MoneyRain/MoneyRain';
import RedDayRain from '../../components/RedDayRain/RedDayRain';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import BrokerAuthExpiredCard from '../../components/BrokerAuthExpiredCard/BrokerAuthExpiredCard';
import { useWebSocket } from '../../context/WebSocketContext';
import styles from './Positions.module.css';

// ─── Market Hours Detection (ET) ────────────────────────────

function isMarketOpen(): boolean {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date());
  const hours = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
  const minutes = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);
  const day = parts.find((p) => p.type === 'weekday')?.value || '';
  if (['Sat', 'Sun'].includes(day)) return false;
  const t = hours * 60 + minutes;
  return t >= 570 && t < 960; // 9:30 AM = 570, 4:00 PM = 960
}

const REFRESH_MARKET = 15_000; // 15s during market hours
const REFRESH_OFF = 60_000; // 60s outside market hours

function tfLabel(tf?: string): string {
  switch (tf) {
    case '1Min':
      return '1m';
    case '5Min':
      return '5m';
    case '15Min':
      return '15m';
    case '1Hour':
      return '1H';
    default:
      return '5m';
  }
}

// Position-aware intraday signal — always renders something so you can read
// the row at a glance.
//   CONFIRM (✓ green) — short-term EMA trend agrees with the position direction
//   WARN    (⚠ red)   — short-term EMA trend disagrees with the position direction
//   NEUTRAL (— gray)  — trend is flat (no directional read), or data missing
// Strength data (RSI extreme, momentum magnitude) lives in the tooltip so
// users can dig deeper when they want.
interface IntradayBadgeSignal {
  kind: 'CONFIRM' | 'WARN' | 'NEUTRAL';
  stale: boolean;
  timeframe?: string;
  tooltip: string;
}

function computeIntradayBadge(
  side: 'long' | 'short',
  intra: NonNullable<import('../../services/AlpacaService').PositionIntelEntry['intraday']>,
  marketOpen: boolean,
): IntradayBadgeSignal {
  const MOMENTUM_THRESHOLD = 0.5; // %
  const RSI_EXTREME_LOW = 30;
  const RSI_EXTREME_HIGH = 70;

  const trend = intra.ema_trend;
  const momentum = intra.momentum_pct ?? 0;
  const rsi = intra.rsi14;

  const tf = tfLabel(intra.timeframe);
  const rsiStr = rsi != null ? rsi.toFixed(0) : '—';
  const momStr = momentum.toFixed(2);
  const staleSuffix = marketOpen ? '' : ' (last session close — market closed)';
  const base = `${tf} ${trend} · RSI ${rsiStr} · 20-bar momentum ${momStr}%`;

  // Strength annotation — included in the tooltip so users can see why the
  // signal is meaningful or just noise.
  const strongMomentum =
    (trend === 'BULLISH' && momentum > MOMENTUM_THRESHOLD) || (trend === 'BEARISH' && momentum < -MOMENTUM_THRESHOLD);
  const rsiExtreme = rsi != null && (rsi <= RSI_EXTREME_LOW || rsi >= RSI_EXTREME_HIGH);
  const strengthNote = strongMomentum
    ? ' · STRONG momentum'
    : rsiExtreme
      ? ' · RSI extreme'
      : ' · weak — read with caution';

  if (trend === 'FLAT') {
    return {
      kind: 'NEUTRAL',
      stale: !marketOpen,
      timeframe: intra.timeframe,
      tooltip: `${tf} trend is flat — no directional read · RSI ${rsiStr}${staleSuffix}`,
    };
  }

  const withLong = trend === 'BULLISH';
  const withShort = trend === 'BEARISH';
  const confirmsPosition = (side === 'long' && withLong) || (side === 'short' && withShort);

  if (confirmsPosition) {
    return {
      kind: 'CONFIRM',
      stale: !marketOpen,
      timeframe: intra.timeframe,
      tooltip: `Confirming the ${side}: ${base}${strengthNote}${staleSuffix}`,
    };
  }
  return {
    kind: 'WARN',
    stale: !marketOpen,
    timeframe: intra.timeframe,
    tooltip: `Against the ${side} — possible exit signal: ${base}${strengthNote}${staleSuffix}`,
  };
}

// ─── Badge Legend ───────────────────────────────────────────

const LEGEND_STORAGE_KEY = 'positionsBadgeLegend.collapsed';

function BadgeLegend(): React.ReactElement {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LEGEND_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LEGEND_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // localStorage may be unavailable in private mode — silent
      }
      return next;
    });
  };

  const chip = (color: string, label: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '1px 6px',
    fontSize: '0.65rem',
    fontWeight: 700,
    color,
    background: `${color}1f`,
    border: `1px solid ${color}59`,
    borderRadius: '3px',
    marginRight: '6px',
    minWidth: '36px',
    textAlign: 'center',
  });

  const items: { badge: React.ReactNode; desc: string }[] = [
    {
      badge: <span style={chip('#27ae60', 'CONFIRM')}>✓ 5m</span>,
      desc: '5-min trend agrees with your position direction',
    },
    { badge: <span style={chip('#e74c3c', 'WARN')}>⚠ 5m</span>, desc: '5-min trend disagrees — possible exit signal' },
    { badge: <span style={chip('#95a5a6', 'NEUTRAL')}>— 5m</span>, desc: '5-min trend is flat — no directional read' },
    { badge: <span style={chip('#f39c12', 'EARNINGS')}>ER Nd</span>, desc: 'Earnings in N trading days (≤5)' },
    { badge: <span style={chip('#e67e22', 'IV')}>IV NN</span>, desc: 'High implied volatility — premium-rich' },
    {
      badge: <span style={chip('#c0392b', 'SI HIGH')}>SI N%</span>,
      desc: 'High short interest — squeeze risk on longs, crowded shorts',
    },
    { badge: <span style={chip('#d35400', 'SI ELEV')}>SI N%</span>, desc: 'Elevated short interest' },
  ];

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '8px',
        padding: collapsed ? '6px 12px' : '10px 14px',
        margin: '12px 0',
        fontSize: '0.78rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          marginBottom: collapsed ? 0 : '8px',
        }}
        onClick={toggle}
      >
        <span
          style={{
            fontWeight: 700,
            opacity: 0.75,
            fontSize: '0.72rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          Badge Legend
        </span>
        <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>{collapsed ? 'Show' : 'Hide'}</span>
      </div>
      {!collapsed && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '8px 16px',
          }}
        >
          {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'baseline', lineHeight: '1.4' }}>
              {item.badge}
              <span style={{ opacity: 0.75 }}>{item.desc}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Option Symbol Parsing ──────────────────────────────────

interface ParsedOption {
  underlying: string;
  expiration: string; // "2025-03-05"
  expirationShort: string; // "Mar 5"
  type: string; // "Call" or "Put"
  strike: number;
}

function parseOptionSymbol(symbol: string): ParsedOption | null {
  // OCC format: SPY250305P00580000
  const match = symbol.match(/^([A-Z]+)(\d{6})([PC])(\d{8})$/);
  if (!match) return null;
  const [, underlying, dateStr, type, strikeStr] = match;
  const year = 2000 + parseInt(dateStr.slice(0, 2), 10);
  const month = parseInt(dateStr.slice(2, 4), 10);
  const day = parseInt(dateStr.slice(4, 6), 10);
  const expiration = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const expirationShort = `${monthNames[month - 1]} ${day}`;
  const strike = parseInt(strikeStr, 10) / 1000;
  return { underlying, expiration, expirationShort, type: type === 'C' ? 'Call' : 'Put', strike };
}

/** Format option symbol for display: "SPY $580 Put" */
function formatOptionLeg(symbol: string): string {
  const parsed = parseOptionSymbol(symbol);
  if (!parsed) return symbol;
  return `${parsed.underlying} $${parsed.strike} ${parsed.type}`;
}

// ─── Spread Grouping ────────────────────────────────────────

interface SpreadGroup {
  key: string;
  underlying: string;
  expiration: string;
  expirationShort: string;
  legs: Position[];
  totalPnl: number;
  totalMarketValue: number;
}

function groupPositions(positions: Position[]): { stocks: Position[]; spreads: SpreadGroup[] } {
  const stocks: Position[] = [];
  const optionMap: Record<string, Position[]> = {};

  for (const pos of positions) {
    if (pos.asset_class === 'us_option') {
      const parsed = parseOptionSymbol(pos.symbol);
      if (parsed) {
        const key = `${parsed.underlying}-${parsed.expiration}`;
        if (!optionMap[key]) optionMap[key] = [];
        optionMap[key].push(pos);
      } else {
        stocks.push(pos);
      }
    } else {
      stocks.push(pos);
    }
  }

  const spreads: SpreadGroup[] = Object.entries(optionMap).map(([key, legs]) => {
    const parsed = parseOptionSymbol(legs[0].symbol)!;
    // Sort legs: puts first (low to high strike), then calls (low to high strike)
    legs.sort((a, b) => {
      const pa = parseOptionSymbol(a.symbol)!;
      const pb = parseOptionSymbol(b.symbol)!;
      if (pa.type !== pb.type) return pa.type === 'Put' ? -1 : 1;
      return pa.strike - pb.strike;
    });
    return {
      key,
      underlying: parsed.underlying,
      expiration: parsed.expiration,
      expirationShort: parsed.expirationShort,
      legs,
      totalPnl: legs.reduce((sum, l) => sum + parseFloat(l.unrealized_pl || '0'), 0),
      totalMarketValue: legs.reduce((sum, l) => sum + parseFloat(l.market_value || '0'), 0),
    };
  });

  return { stocks, spreads };
}

// ─── Component ───────────────────────────────────────────────

const Positions: React.FC = () => {
  const { mode, isLive } = useAccountMode();
  const { isAdmin } = useAuth();
  const { isDemoMode, exitDemo } = useDemo();
  const navigate = useNavigate();
  const { connected, on } = useWebSocket();

  // Data state
  const [account, setAccount] = useState<Account | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [brokerAuthExpired, setBrokerAuthExpired] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);

  // P&L animations — trigger once on first load
  const [showMoneyRain, setShowMoneyRain] = useState(false);
  const [showRedDayRain, setShowRedDayRain] = useState(false);
  const pnlAnimFired = useRef(false);

  // Close position state (stocks)
  const [closingSymbol, setClosingSymbol] = useState<string | null>(null);
  const [closeQty, setCloseQty] = useState<number | null>(null); // null = close full position
  const [closeInProgress, setCloseInProgress] = useState<string | null>(null);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Open orders
  const [orders, setOrders] = useState<any[]>([]);
  const [cancellingOrder, setCancellingOrder] = useState<string | null>(null);

  // RSI data for position symbols
  const [rsiData, setRsiData] = useState<Record<string, ScanResult>>({});
  const [intelData, setIntelData] = useState<Record<string, PositionIntelEntry>>({});

  // TP/SL targets
  const [targetsData, setTargetsData] = useState<Record<string, PositionTarget>>({});
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [expandedTargets, setExpandedTargets] = useState<Record<string, boolean>>({});

  // Analyze state
  const [analyzingSymbols, setAnalyzingSymbols] = useState<Set<string>>(new Set());
  const [analyzeErrors, setAnalyzeErrors] = useState<Set<string>>(new Set());
  const [analyzeAllRunning, setAnalyzeAllRunning] = useState(false);
  const [analyzeAllProgress, setAnalyzeAllProgress] = useState<{ current: number; total: number } | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const recheckRef = useRef<ReturnType<typeof setInterval>>();

  // ── Sorting ────────────────────────────────────────────────

  type SortKey =
    | 'symbol'
    | 'side'
    | 'qty'
    | 'avg_entry_price'
    | 'current_price'
    | 'unrealized_pl'
    | 'unrealized_plpc'
    | 'change_today'
    | 'market_value';
  const [sortKey, setSortKey] = useState<SortKey>('unrealized_pl');
  const [sortAsc, setSortAsc] = useState(false);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === 'symbol' || key === 'side'); // alpha cols default asc
    }
  };

  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '');

  // Group positions into stocks and spreads
  const { stocks, spreads } = useMemo(() => groupPositions(positions), [positions]);

  // Sort stocks
  const sortedStocks = useMemo(() => {
    const copy = [...stocks];
    copy.sort((a, b) => {
      let av: number | string, bv: number | string;
      if (sortKey === 'symbol' || sortKey === 'side') {
        av = (a[sortKey] || '').toLowerCase();
        bv = (b[sortKey] || '').toLowerCase();
      } else if (sortKey === 'unrealized_plpc' || sortKey === 'change_today') {
        av = parseFloat(a[sortKey] || '0') * 100;
        bv = parseFloat(b[sortKey] || '0') * 100;
      } else {
        av = parseFloat(String(a[sortKey] || '0'));
        bv = parseFloat(String(b[sortKey] || '0'));
      }
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });
    return copy;
  }, [stocks, sortKey, sortAsc]);

  // ── Fetch data ──────────────────────────────────────────────

  const fetchData = useCallback(
    async (background = false) => {
      if (isDemoMode) {
        setAccount(DEMO_ACCOUNT);
        setPositions(DEMO_POSITIONS);
        setLastUpdated(new Date());
        setLoading(false);
        return;
      }
      if (background) setIsRefreshing(true);
      try {
        const [acct, pos, ordersData] = await Promise.all([
          alpacaService.getAccount(mode),
          alpacaService.getPositions(mode),
          alpacaService.getOrders('open', '50', undefined, undefined, mode),
        ]);
        setAccount(acct);
        setPositions(pos.positions);
        setOrders(ordersData.orders || ordersData || []);
        setLastUpdated(new Date());
        setError('');
        setBrokerAuthExpired(false);
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
        setBrokerAuthExpired(Boolean(err?.brokerAuthExpired));
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [mode, isDemoMode],
  );

  // ── P&L animation on first load ────────────────────────────

  useEffect(() => {
    if (pnlAnimFired.current || !account) return;
    const dailyPnl = parseFloat(account.daily_pnl || '0');
    if (dailyPnl > 0) {
      pnlAnimFired.current = true;
      setShowMoneyRain(true);
    } else if (dailyPnl < 0) {
      pnlAnimFired.current = true;
      setShowRedDayRain(true);
    }
  }, [account]);

  // ── Auto-refresh timer ──────────────────────────────────────

  useEffect(() => {
    fetchData();

    const startInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const ms = isMarketOpen() ? REFRESH_MARKET : REFRESH_OFF;
      intervalRef.current = setInterval(() => fetchData(true), ms);
    };

    startInterval();

    // Re-evaluate interval every 60s to catch market open/close
    recheckRef.current = setInterval(startInterval, 60_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (recheckRef.current) clearInterval(recheckRef.current);
    };
  }, [fetchData]);

  // ── WebSocket: instant refresh on position/order events ────
  useEffect(() => {
    const unsub1 = on('position_update', () => fetchData(true));
    const unsub2 = on('order_fill', () => fetchData(true));
    const unsub3 = on('bot_update', () => fetchData(true));
    return () => {
      unsub1();
      unsub2();
      unsub3();
    };
  }, [on, fetchData]);

  // ── "Updated Xs ago" counter ────────────────────────────────

  useEffect(() => {
    if (!lastUpdated) return;
    setSecondsAgo(0);
    const tick = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [lastUpdated]);

  // ── Fetch RSI data for stock positions ─────────────────────

  const stockSymbols = useMemo(
    () =>
      stocks
        .map((p) => p.symbol)
        .sort()
        .join(','),
    [stocks],
  );

  useEffect(() => {
    if (!stockSymbols) return;
    let cancelled = false;
    const fetchRsi = async () => {
      try {
        const res = await alpacaService.scanRsi(stockSymbols);
        if (cancelled) return;
        const map: Record<string, ScanResult> = {};
        for (const r of res.results) map[r.symbol] = r;
        setRsiData(map);
      } catch {
        // non-critical — silently ignore
      }
    };
    fetchRsi();
    const ms = isMarketOpen() ? 30_000 : 120_000;
    const id = setInterval(fetchRsi, ms);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [stockSymbols]);

  // ── Fetch intraday + earnings intel every 5 min ────────────

  useEffect(() => {
    if (!stockSymbols) {
      setIntelData({});
      return;
    }
    let cancelled = false;
    const syms = stockSymbols.split(',').filter(Boolean);
    const fetchIntel = async () => {
      try {
        const res = await alpacaService.getPositionIntel(syms);
        if (cancelled) return;
        setIntelData(res.intel || {});
      } catch {
        // non-critical — silently ignore
      }
    };
    fetchIntel();
    const id = setInterval(fetchIntel, 5 * 60_000); // every 5 min
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [stockSymbols]);

  // ── Fetch TP/SL targets for stock positions ────────────────

  useEffect(() => {
    if (!stockSymbols) {
      setTargetsData({});
      return;
    }
    let cancelled = false;
    const fetchTargets = async () => {
      setTargetsLoading(true);
      try {
        const syms = stockSymbols.split(',');
        const data = await alpacaService.getPositionTargets(syms);
        if (!cancelled) setTargetsData(data);
      } catch {
        // non-critical — silently ignore
      } finally {
        if (!cancelled) setTargetsLoading(false);
      }
    };
    fetchTargets();
    const ms = isMarketOpen() ? 30_000 : 120_000;
    const id = setInterval(fetchTargets, ms);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [stockSymbols]);

  // ── Toast auto-dismiss ──────────────────────────────────────

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Close single position handler (stocks) ─────────────────

  const handleConfirmClose = async () => {
    if (!closingSymbol) return;
    setCloseInProgress(closingSymbol);
    try {
      const opts = closeQty != null && closeQty > 0 ? { qty: closeQty } : undefined;
      const result = await alpacaService.closePosition(closingSymbol, mode, opts);
      if (result.error) throw new Error(result.error);
      const msg =
        opts?.qty != null ? `Closed ${opts.qty} ${closingSymbol} (partial)` : `Closed ${closingSymbol} position`;
      setToast({ message: msg, type: 'success' });
      setClosingSymbol(null);
      setCloseQty(null);
      fetchData(true);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to close position', type: 'error' });
    } finally {
      setCloseInProgress(null);
    }
  };

  // ── Cancel order handler ────────────────────────────────────

  const handleCancelOrder = async (orderId: string) => {
    setCancellingOrder(orderId);
    try {
      await alpacaService.cancelOrder(orderId, mode);
      setToast({ message: 'Order cancelled', type: 'success' });
      fetchData(true);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to cancel order', type: 'error' });
    } finally {
      setCancellingOrder(null);
    }
  };

  // ── Analyze position handler ────────────────────────────────

  const refreshTargets = useCallback(async () => {
    if (!stockSymbols) return;
    try {
      const syms = stockSymbols.split(',');
      const data = await alpacaService.getPositionTargets(syms);
      setTargetsData(data);
    } catch {
      // non-critical
    }
  }, [stockSymbols]);

  const handleAnalyze = useCallback(
    async (symbol: string, side: string) => {
      setAnalyzingSymbols((prev) => new Set(prev).add(symbol));
      setAnalyzeErrors((prev) => {
        const next = new Set(prev);
        next.delete(symbol);
        return next;
      });
      try {
        await alpacaService.getTradeAnalysis(symbol, side, 'analyze_position', 'existing');
        await refreshTargets();
        setExpandedTargets((prev) => ({ ...prev, [symbol]: true }));
      } catch (err: any) {
        setAnalyzeErrors((prev) => new Set(prev).add(symbol));
        setToast({ message: `Analysis failed for ${symbol}: ${err.message || 'Unknown error'}`, type: 'error' });
        // Clear error flash after 3 seconds
        setTimeout(() => {
          setAnalyzeErrors((prev) => {
            const next = new Set(prev);
            next.delete(symbol);
            return next;
          });
        }, 3000);
      } finally {
        setAnalyzingSymbols((prev) => {
          const next = new Set(prev);
          next.delete(symbol);
          return next;
        });
      }
    },
    [refreshTargets],
  );

  const handleAnalyzeAll = useCallback(async () => {
    if (analyzeAllRunning || stocks.length === 0) return;
    setAnalyzeAllRunning(true);
    setAnalyzeAllProgress({ current: 0, total: stocks.length });
    for (let i = 0; i < stocks.length; i++) {
      setAnalyzeAllProgress({ current: i + 1, total: stocks.length });
      await handleAnalyze(stocks[i].symbol, stocks[i].side);
    }
    setAnalyzeAllProgress(null);
    setAnalyzeAllRunning(false);
  }, [analyzeAllRunning, stocks, handleAnalyze]);

  // ── Helpers ─────────────────────────────────────────────────

  const fmt = (val: string | number | undefined, decimals = 2) => {
    const num = typeof val === 'number' ? val : parseFloat(String(val || '0'));
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const pnlClass = (val: string | number | undefined) => {
    const num = typeof val === 'number' ? val : parseFloat(String(val || '0'));
    return num > 0 ? styles.positive : num < 0 ? styles.negative : '';
  };

  const rsiColor = (val: number | null | undefined): string | undefined => {
    if (val == null) return undefined;
    if (val < 10) return '#e74c3c';
    if (val < 30) return '#ff9800';
    if (val > 90) return '#2ecc71';
    if (val > 70) return '#ff9800';
    return undefined;
  };

  const rsiWeight = (val: number | null | undefined): number | undefined => {
    if (val == null) return undefined;
    if (val < 10 || val > 90) return 700;
    return undefined;
  };

  const signalFromRsi2 = (val: number | null | undefined): string => {
    if (val == null) return 'NEUTRAL';
    if (val < 10) return 'OVERSOLD';
    if (val < 30) return 'WEAK';
    if (val > 90) return 'OVERBOUGHT';
    if (val > 70) return 'STRONG';
    return 'NEUTRAL';
  };

  const signalBadgeClass = (signal: string): string => {
    switch (signal) {
      case 'OVERSOLD':
        return styles.signalOversold;
      case 'WEAK':
        return styles.signalWeak;
      case 'OVERBOUGHT':
        return styles.signalOverbought;
      case 'STRONG':
        return styles.signalStrong;
      default:
        return styles.signalNeutral;
    }
  };

  const toggleTargets = (symbol: string) => {
    setExpandedTargets((prev) => ({ ...prev, [symbol]: !prev[symbol] }));
  };

  /** Compute percentage distance from current price to a level */
  const pctFromPrice = (current: number, level: number | null): string | null => {
    if (level == null || current === 0) return null;
    const pct = ((level - current) / current) * 100;
    return pct >= 0 ? `+${pct.toFixed(1)}%` : `${pct.toFixed(1)}%`;
  };

  /** Render the mini progress bar + target levels for a position */
  const renderTargetBar = (symbol: string, pos: Position) => {
    const target = targetsData[symbol];
    if (!target) return null;

    const currentPrice = parseFloat(pos.current_price || '0');
    const isShort = pos.side === 'short';

    // For the progress bar, determine range from SL to TP3 (or TP1 if no TP3)
    const sl = target.stopLoss;
    const tp1 = target.takeProfit;
    const tp3 = target.takeProfit3 ?? target.takeProfit2 ?? target.takeProfit;

    if (sl == null && tp1 == null) return null;

    // For SHORT positions: SL is above entry, TPs are below.
    // Normalize: barMin is the lowest price, barMax is the highest.
    const allLevels = [sl, tp1, target.takeProfit2, tp3, currentPrice].filter((v): v is number => v != null);
    const barMin = Math.min(...allLevels);
    const barMax = Math.max(...allLevels);
    const range = barMax - barMin || 1;

    const pctPos = (val: number) => ((val - barMin) / range) * 100;

    const barBg = 'linear-gradient(90deg, rgba(231,76,60,0.25), rgba(46,204,113,0.25))';
    const barBgShort = 'linear-gradient(90deg, rgba(46,204,113,0.25), rgba(231,76,60,0.25))';

    return (
      <div style={{ padding: '8px 0' }}>
        {/* Progress bar */}
        <div
          style={{
            position: 'relative',
            height: '8px',
            borderRadius: '4px',
            background: isShort ? barBgShort : barBg,
            marginBottom: '8px',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          {/* SL marker */}
          {sl != null && (
            <div
              style={{
                position: 'absolute',
                left: `${pctPos(sl)}%`,
                top: '-3px',
                width: '2px',
                height: '14px',
                background: '#e74c3c',
                borderRadius: '1px',
              }}
              title={`SL: $${sl.toFixed(2)}`}
            />
          )}
          {/* TP1 marker */}
          {tp1 != null && (
            <div
              style={{
                position: 'absolute',
                left: `${pctPos(tp1)}%`,
                top: '-3px',
                width: '2px',
                height: '14px',
                background: '#2ecc71',
                borderRadius: '1px',
              }}
              title={`TP1: $${tp1.toFixed(2)}`}
            />
          )}
          {/* TP2 marker */}
          {target.takeProfit2 != null && (
            <div
              style={{
                position: 'absolute',
                left: `${pctPos(target.takeProfit2)}%`,
                top: '-3px',
                width: '2px',
                height: '14px',
                background: 'rgba(46,204,113,0.6)',
                borderRadius: '1px',
              }}
              title={`TP2: $${target.takeProfit2.toFixed(2)}`}
            />
          )}
          {/* TP3 marker */}
          {target.takeProfit3 != null && (
            <div
              style={{
                position: 'absolute',
                left: `${pctPos(target.takeProfit3)}%`,
                top: '-3px',
                width: '2px',
                height: '14px',
                background: 'rgba(46,204,113,0.35)',
                borderRadius: '1px',
              }}
              title={`TP3: $${target.takeProfit3.toFixed(2)}`}
            />
          )}
          {/* Current price dot */}
          <div
            style={{
              position: 'absolute',
              left: `${pctPos(currentPrice)}%`,
              top: '-2px',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: '#3b82f6',
              border: '2px solid #1e293b',
              transform: 'translateX(-6px)',
              zIndex: 2,
            }}
            title={`Current: $${currentPrice.toFixed(2)}`}
          />
        </div>

        {/* Level labels */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '0.75rem', lineHeight: '1.4' }}>
          {sl != null && (
            <span style={{ color: '#e74c3c' }}>
              SL ${sl.toFixed(2)}
              <span style={{ opacity: 0.6, marginLeft: '3px' }}>{pctFromPrice(currentPrice, sl)}</span>
            </span>
          )}
          {tp1 != null && (
            <span style={{ color: '#2ecc71' }}>
              TP1 ${tp1.toFixed(2)}
              <span style={{ opacity: 0.6, marginLeft: '3px' }}>{pctFromPrice(currentPrice, tp1)}</span>
            </span>
          )}
          {target.takeProfit2 != null && (
            <span style={{ color: 'rgba(46,204,113,0.7)' }}>
              TP2 ${target.takeProfit2.toFixed(2)}
              <span style={{ opacity: 0.6, marginLeft: '3px' }}>{pctFromPrice(currentPrice, target.takeProfit2)}</span>
            </span>
          )}
          {target.takeProfit3 != null && (
            <span style={{ color: 'rgba(46,204,113,0.45)' }}>
              TP3 ${target.takeProfit3.toFixed(2)}
              <span style={{ opacity: 0.6, marginLeft: '3px' }}>{pctFromPrice(currentPrice, target.takeProfit3)}</span>
            </span>
          )}
          {/* Source badge */}
          <span
            style={{
              display: 'inline-block',
              padding: '1px 6px',
              borderRadius: '4px',
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: '0.5px',
              background: target.source === 'strategist' ? 'rgba(59,130,246,0.15)' : 'rgba(139,92,246,0.15)',
              color: target.source === 'strategist' ? '#60a5fa' : '#a78bfa',
              border: `1px solid ${target.source === 'strategist' ? 'rgba(59,130,246,0.25)' : 'rgba(139,92,246,0.25)'}`,
              marginLeft: 'auto',
            }}
          >
            {target.source === 'strategist' ? 'STRAT' : 'QT'}
          </span>
        </div>
      </div>
    );
  };

  const closingPosition = closingSymbol ? positions.find((p) => p.symbol === closingSymbol) : null;

  // ── Render ──────────────────────────────────────────────────

  if (loading) {
    return (
      <section className={styles.page}>
        <SignalLoader message="Loading positions..." fullPage />
      </section>
    );
  }

  if (error && !account) {
    if (brokerAuthExpired) {
      return (
        <section className={styles.page}>
          <BrokerAuthExpiredCard mode={mode} variant="full" />
        </section>
      );
    }
    return (
      <section className={styles.page}>
        <p className={styles.error}>{error}</p>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      {isDemoMode && (
        <div
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            padding: '10px 20px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ color: '#fff', fontSize: '0.9rem' }}>
            You're viewing demo data — sign up and connect your broker for live trading
          </span>
          <button
            onClick={() => {
              exitDemo();
              navigate('/onboarding');
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              padding: '6px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Exit Demo
          </button>
        </div>
      )}
      <MoneyRain active={showMoneyRain} />
      <RedDayRain active={showRedDayRain} />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Positions</h1>
          {!isAdmin && <span className={styles.viewerBadge}>VIEW ONLY</span>}
        </div>
        <div className={styles.updatedInfo}>
          {isRefreshing && <span className={styles.refreshDot} />}
          {lastUpdated && <span className={styles.updated}>Updated {secondsAgo}s ago</span>}
          <span className={styles.marketStatus}>{isMarketOpen() ? '🟢 Market Open' : '🔴 Market Closed'}</span>
        </div>
      </div>

      {/* Account summary */}
      {account && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Equity</span>
            <span className={styles.statValue}>${fmt(account.equity)}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Daily P&L</span>
            <span className={`${styles.statValue} ${pnlClass(account.daily_pnl)}`}>
              ${fmt(account.daily_pnl)} ({account.daily_pnl_pct}%)
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Buying Power</span>
            <span className={styles.statValue}>${fmt(account.buying_power)}</span>
          </div>
        </div>
      )}

      {/* Badge legend — explains the inline indicators on each row */}
      <BadgeLegend />

      {/* Positions table */}
      {positions.length === 0 ? (
        <p className={styles.empty}>No open positions</p>
      ) : (
        <div className={styles.tableWrap}>
          {/* Analyze All button */}
          {stocks.length > 0 && isAdmin && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
              <button className={styles.analyzeAllBtn} onClick={handleAnalyzeAll} disabled={analyzeAllRunning}>
                {analyzeAllRunning && analyzeAllProgress
                  ? `Analyzing ${analyzeAllProgress.current}/${analyzeAllProgress.total}...`
                  : 'Analyze All'}
              </button>
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.sortable} onClick={() => handleSort('symbol')}>
                  Symbol{sortIndicator('symbol')}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('side')}>
                  Side{sortIndicator('side')}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('qty')}>
                  Qty{sortIndicator('qty')}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('avg_entry_price')}>
                  Entry Price{sortIndicator('avg_entry_price')}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('current_price')}>
                  Current Price{sortIndicator('current_price')}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('unrealized_pl')}>
                  Unrealized P&L{sortIndicator('unrealized_pl')}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('unrealized_plpc')}>
                  P&L %{sortIndicator('unrealized_plpc')}
                </th>
                <th className={styles.rsiCol}>RSI(2)</th>
                <th className={styles.rsiCol}>RSI(6)</th>
                <th className={styles.rsiCol}>RSI(14)</th>
                <th className={styles.rsiCol}>Signal</th>
                <th className={styles.sortable} onClick={() => handleSort('change_today')}>
                  Day Change{sortIndicator('change_today')}
                </th>
                <th className={styles.sortable} onClick={() => handleSort('market_value')}>
                  Market Value{sortIndicator('market_value')}
                </th>
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {/* ── Stock positions ── */}
              {sortedStocks.map((pos) => (
                <React.Fragment key={pos.symbol}>
                  <tr
                    style={{ cursor: targetsData[pos.symbol] || targetsLoading ? 'pointer' : undefined }}
                    onClick={() => {
                      if (targetsData[pos.symbol]) toggleTargets(pos.symbol);
                    }}
                  >
                    <td className={styles.symbol}>
                      {pos.symbol}
                      {(() => {
                        const intel = intelData[pos.symbol];
                        if (!intel) return null;
                        const intra = intel.intraday;
                        const earn = intel.earnings;
                        const marketOpen = isMarketOpen();
                        const badge = intra
                          ? computeIntradayBadge(pos.side as 'long' | 'short', intra, marketOpen)
                          : null;
                        const baseBadgeStyle = (color: string, stale: boolean) => ({
                          display: 'inline-block' as const,
                          marginLeft: '6px',
                          padding: '1px 5px',
                          fontSize: '0.6rem',
                          fontWeight: 700 as const,
                          color,
                          background: stale ? 'transparent' : `${color}1f`,
                          border: `1px solid ${stale ? color + '4d' : color + '59'}`,
                          borderRadius: '3px',
                          opacity: stale ? 0.55 : 1,
                          cursor: 'help' as const,
                        });
                        const opts = intel.options;
                        return (
                          <>
                            {earn?.status === 'WARN' && (
                              <span
                                title={earn.message + (earn.date ? ` — report ${earn.date}` : '')}
                                style={baseBadgeStyle('#f39c12', false)}
                              >
                                ER {earn.tradingDays}d
                              </span>
                            )}
                            {badge?.kind === 'CONFIRM' && (
                              <span title={badge.tooltip} style={baseBadgeStyle('#27ae60', badge.stale)}>
                                ✓ {tfLabel(badge.timeframe)}
                              </span>
                            )}
                            {badge?.kind === 'WARN' && (
                              <span title={badge.tooltip} style={baseBadgeStyle('#e74c3c', badge.stale)}>
                                ⚠ {tfLabel(badge.timeframe)}
                              </span>
                            )}
                            {badge?.kind === 'NEUTRAL' && (
                              <span title={badge.tooltip} style={baseBadgeStyle('#95a5a6', badge.stale)}>
                                — {tfLabel(badge.timeframe)}
                              </span>
                            )}
                            {opts && opts.iv_class === 'HIGH' && (
                              <span
                                title={`ATM IV ${opts.atm_iv_pct}% (HIGH) · P/C ${opts.put_call_ratio ?? '—'} (${opts.pc_signal})${opts.near_dte ? ' · ' + opts.near_dte + 'DTE' : ''}`}
                                style={baseBadgeStyle('#e67e22', false)}
                              >
                                IV {Math.round(opts.atm_iv_pct)}
                              </span>
                            )}
                            {intel.shortInterest &&
                              intel.shortInterest.bucket !== 'NORMAL' &&
                              (() => {
                                const si = intel.shortInterest!;
                                const bucketColor =
                                  si.bucket === 'HIGH' ? '#c0392b' : si.bucket === 'ELEVATED' ? '#d35400' : '#16a085'; // LOW — clean float, green
                                const bucketMsg =
                                  si.bucket === 'LOW'
                                    ? 'Clean float — no squeeze fuel, shorts viable, mean-reversion clean.'
                                    : pos.side === 'long'
                                      ? 'Squeeze tailwind possible.'
                                      : 'Crowded short — risk of sharp reversal.';
                                const changeStr =
                                  si.pct_change != null
                                    ? ` · ΔSI ${si.pct_change >= 0 ? '+' : ''}${si.pct_change}%`
                                    : '';
                                return (
                                  <span
                                    title={`Short interest: ${si.pct_float ?? '?'}% of float, ${si.days_to_cover ?? '?'}d to cover (${si.bucket})${changeStr}. ${bucketMsg}`}
                                    style={baseBadgeStyle(bucketColor, false)}
                                  >
                                    SI{' '}
                                    {si.pct_float != null
                                      ? `${Math.round(si.pct_float)}%`
                                      : si.days_to_cover != null
                                        ? `${si.days_to_cover}d`
                                        : '!'}
                                  </span>
                                );
                              })()}
                          </>
                        );
                      })()}
                      {targetsData[pos.symbol] && (
                        <span
                          style={{
                            display: 'inline-block',
                            marginLeft: '6px',
                            fontSize: '0.6rem',
                            opacity: 0.4,
                            transition: 'transform 0.15s',
                            transform: expandedTargets[pos.symbol] ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}
                        >
                          &#9654;
                        </span>
                      )}
                    </td>
                    <td className={pos.side === 'long' ? styles.long : styles.short}>{pos.side.toUpperCase()}</td>
                    <td className={styles.numCol}>
                      {parseFloat(pos.qty) % 1 === 0
                        ? parseInt(pos.qty).toLocaleString()
                        : parseFloat(pos.qty).toFixed(2)}
                    </td>
                    <td className={styles.numCol}>${fmt(pos.avg_entry_price)}</td>
                    <td className={styles.numCol}>${fmt(pos.current_price)}</td>
                    <td className={`${styles.numCol} ${pnlClass(pos.unrealized_pl)}`}>${fmt(pos.unrealized_pl)}</td>
                    <td className={`${styles.numCol} ${pnlClass(pos.unrealized_plpc)}`}>
                      {fmt(String(parseFloat(pos.unrealized_plpc || '0') * 100))}%
                    </td>
                    {(() => {
                      const rsi = rsiData[pos.symbol] as ScanResult | undefined;
                      const r2 = rsi?.rsi2 ?? null;
                      const r6 = rsi?.rsi6 ?? null;
                      const r14 = rsi?.rsi14 ?? null;
                      const sig = rsi?.signal ?? signalFromRsi2(r2);
                      return (
                        <>
                          <td className={styles.rsiCell} style={{ color: rsiColor(r2), fontWeight: rsiWeight(r2) }}>
                            {r2 != null ? r2.toFixed(1) : '—'}
                          </td>
                          <td className={styles.rsiCell} style={{ color: rsiColor(r6), fontWeight: rsiWeight(r6) }}>
                            {r6 != null ? r6.toFixed(1) : '—'}
                          </td>
                          <td className={styles.rsiCell} style={{ color: rsiColor(r14), fontWeight: rsiWeight(r14) }}>
                            {r14 != null ? r14.toFixed(1) : '—'}
                          </td>
                          <td className={styles.rsiCell}>
                            <span className={`${styles.signalBadge} ${signalBadgeClass(sig)}`}>{sig}</span>
                          </td>
                        </>
                      );
                    })()}
                    <td className={`${styles.numCol} ${pnlClass(pos.change_today)}`}>
                      {fmt(String(parseFloat(pos.change_today || '0') * 100))}%
                    </td>
                    <td className={styles.numCol}>${fmt(pos.market_value)}</td>
                    {isAdmin && (
                      <td>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button
                            className={`${styles.analyzeBtn} ${analyzeErrors.has(pos.symbol) ? styles.analyzeBtnError : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAnalyze(pos.symbol, pos.side);
                            }}
                            disabled={analyzingSymbols.has(pos.symbol)}
                          >
                            {analyzingSymbols.has(pos.symbol) ? (
                              <>
                                <span className={styles.analyzeSpinner} /> Analyzing
                              </>
                            ) : (
                              'Analyze'
                            )}
                          </button>
                          <button
                            className={styles.closeBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              setClosingSymbol(pos.symbol);
                            }}
                            disabled={closeInProgress === pos.symbol}
                          >
                            {closeInProgress === pos.symbol ? 'Closing...' : 'Close'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {/* Expandable TP/SL target row */}
                  {expandedTargets[pos.symbol] && targetsData[pos.symbol] && (
                    <tr style={{ background: 'rgba(59,130,246,0.03)' }}>
                      <td colSpan={isAdmin ? 14 : 13} style={{ padding: '6px 16px 10px', borderTop: 'none' }}>
                        {renderTargetBar(pos.symbol, pos)}
                      </td>
                    </tr>
                  )}
                  {/* Inline indicator when collapsed — show small badges */}
                  {!expandedTargets[pos.symbol] && !targetsData[pos.symbol] && !targetsLoading && (
                    <tr style={{ height: 0, border: 'none' }}>
                      <td
                        colSpan={isAdmin ? 14 : 13}
                        style={{
                          padding: '0 16px 4px',
                          borderTop: 'none',
                          fontSize: '0.7rem',
                          color: 'rgba(255,255,255,0.25)',
                        }}
                      >
                        No targets — Run analysis
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {/* ── Option spread groups ── */}
              {spreads.map((spread) => (
                <React.Fragment key={spread.key}>
                  {/* Spread header row */}
                  <tr className={styles.spreadHeaderRow}>
                    <td className={styles.spreadHeader} colSpan={5}>
                      <span className={styles.spreadLabel}>OPTIONS SPREAD</span>
                      <strong>{spread.underlying}</strong>
                      <span className={styles.spreadExpiry}>Exp {spread.expirationShort}</span>
                      <span className={styles.spreadLegCount}>{spread.legs.length} legs</span>
                    </td>
                    <td className={pnlClass(spread.totalPnl)}>${fmt(spread.totalPnl)}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>${fmt(spread.totalMarketValue)}</td>
                  </tr>
                  {/* Individual leg rows */}
                  {spread.legs.map((leg) => {
                    const parsed = parseOptionSymbol(leg.symbol);
                    return (
                      <tr key={leg.symbol} className={styles.legRow}>
                        <td className={styles.legSymbol}>
                          {parsed ? `  ${parsed.underlying} $${parsed.strike} ${parsed.type}` : leg.symbol}
                        </td>
                        <td className={leg.side === 'long' ? styles.long : styles.short}>{leg.side.toUpperCase()}</td>
                        <td>{leg.qty}</td>
                        <td>${fmt(leg.avg_entry_price)}</td>
                        <td>${fmt(leg.current_price)}</td>
                        <td className={pnlClass(leg.unrealized_pl)}>${fmt(leg.unrealized_pl)}</td>
                        <td className={pnlClass(leg.unrealized_plpc)}>
                          {fmt(String(parseFloat(leg.unrealized_plpc || '0') * 100))}%
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className={pnlClass(leg.change_today)}>
                          {fmt(String(parseFloat(leg.change_today || '0') * 100))}%
                        </td>
                        <td>${fmt(leg.market_value)}</td>
                        {isAdmin && <td></td>}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile card view */}
      {positions.length > 0 && (
        <div className={styles.mobileCards}>
          {/* Analyze All — mobile */}
          {stocks.length > 0 && isAdmin && (
            <div style={{ marginBottom: '10px' }}>
              <button
                className={styles.analyzeAllBtn}
                onClick={handleAnalyzeAll}
                disabled={analyzeAllRunning}
                style={{ width: '100%' }}
              >
                {analyzeAllRunning && analyzeAllProgress
                  ? `Analyzing ${analyzeAllProgress.current}/${analyzeAllProgress.total}...`
                  : 'Analyze All Positions'}
              </button>
            </div>
          )}
          {sortedStocks.map((pos) => (
            <div key={pos.symbol} className={styles.mobileCard}>
              <div className={styles.mobileCardHeader}>
                <span className={styles.symbol}>
                  {pos.symbol}
                  {(() => {
                    const intel = intelData[pos.symbol];
                    if (!intel) return null;
                    const intra = intel.intraday;
                    const earn = intel.earnings;
                    const marketOpen = isMarketOpen();
                    const badge = intra ? computeIntradayBadge(pos.side as 'long' | 'short', intra, marketOpen) : null;
                    const badgeStyle = (color: string, stale = false) => ({
                      display: 'inline-block',
                      marginLeft: '6px',
                      padding: '1px 5px',
                      fontSize: '0.6rem',
                      fontWeight: 700 as const,
                      color,
                      background: stale ? 'transparent' : `${color}1f`,
                      border: `1px solid ${stale ? color + '4d' : color + '59'}`,
                      borderRadius: '3px',
                      opacity: stale ? 0.55 : 1,
                    });
                    return (
                      <>
                        {earn?.status === 'WARN' && (
                          <span title={earn.message} style={badgeStyle('#f39c12')}>
                            ER {earn.tradingDays}d
                          </span>
                        )}
                        {badge?.kind === 'CONFIRM' && (
                          <span title={badge.tooltip} style={badgeStyle('#27ae60', badge.stale)}>
                            ✓ {tfLabel(badge.timeframe)}
                          </span>
                        )}
                        {badge?.kind === 'WARN' && (
                          <span title={badge.tooltip} style={badgeStyle('#e74c3c', badge.stale)}>
                            ⚠ {tfLabel(badge.timeframe)}
                          </span>
                        )}
                        {badge?.kind === 'NEUTRAL' && (
                          <span title={badge.tooltip} style={badgeStyle('#95a5a6', badge.stale)}>
                            — {tfLabel(badge.timeframe)}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </span>
                <span className={pos.side === 'long' ? styles.long : styles.short}>{pos.side.toUpperCase()}</span>
              </div>
              <div className={styles.mobileCardBody}>
                <div className={styles.mobileCardRow}>
                  <span>Qty</span>
                  <span>{pos.qty}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span>Entry</span>
                  <span>${fmt(pos.avg_entry_price)}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span>Current</span>
                  <span>${fmt(pos.current_price)}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span>P&L</span>
                  <span className={pnlClass(pos.unrealized_pl)}>
                    ${fmt(pos.unrealized_pl)} ({fmt(String(parseFloat(pos.unrealized_plpc || '0') * 100))}%)
                  </span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span>Day</span>
                  <span className={pnlClass(pos.change_today)}>
                    {fmt(String(parseFloat(pos.change_today || '0') * 100))}%
                  </span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span>Value</span>
                  <span>${fmt(pos.market_value)}</span>
                </div>
                {(() => {
                  const rsi = rsiData[pos.symbol] as ScanResult | undefined;
                  const r2 = rsi?.rsi2 ?? null;
                  const r6 = rsi?.rsi6 ?? null;
                  const r14 = rsi?.rsi14 ?? null;
                  const sig = rsi?.signal ?? signalFromRsi2(r2);
                  return (
                    <div className={styles.mobileRsiRow}>
                      <span style={{ color: rsiColor(r2), fontWeight: rsiWeight(r2) }}>
                        RSI(2): {r2 != null ? r2.toFixed(1) : '—'}
                      </span>
                      <span style={{ color: rsiColor(r6), fontWeight: rsiWeight(r6) }}>
                        RSI(6): {r6 != null ? r6.toFixed(1) : '—'}
                      </span>
                      <span style={{ color: rsiColor(r14), fontWeight: rsiWeight(r14) }}>
                        RSI(14): {r14 != null ? r14.toFixed(1) : '—'}
                      </span>
                      <span className={`${styles.signalBadge} ${signalBadgeClass(sig)}`}>{sig}</span>
                    </div>
                  );
                })()}
                {/* TP/SL targets — mobile */}
                {targetsData[pos.symbol] ? (
                  <div
                    onClick={() => toggleTargets(pos.symbol)}
                    style={{
                      marginTop: '8px',
                      padding: '8px',
                      borderRadius: '6px',
                      background: 'rgba(59,130,246,0.05)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.7rem',
                        color: 'rgba(255,255,255,0.4)',
                        marginBottom: expandedTargets[pos.symbol] ? '4px' : 0,
                      }}
                    >
                      TP/SL Targets {expandedTargets[pos.symbol] ? '▾' : '▸'}
                    </div>
                    {expandedTargets[pos.symbol] && renderTargetBar(pos.symbol, pos)}
                  </div>
                ) : !targetsLoading ? (
                  <div style={{ marginTop: '6px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)' }}>
                    No targets — Run analysis
                  </div>
                ) : null}
              </div>
              {isAdmin && (
                <div className={styles.mobileCardActions}>
                  <button
                    className={`${styles.analyzeBtn} ${analyzeErrors.has(pos.symbol) ? styles.analyzeBtnError : ''}`}
                    onClick={() => handleAnalyze(pos.symbol, pos.side)}
                    disabled={analyzingSymbols.has(pos.symbol)}
                  >
                    {analyzingSymbols.has(pos.symbol) ? (
                      <>
                        <span className={styles.analyzeSpinner} /> Analyzing
                      </>
                    ) : (
                      'Analyze'
                    )}
                  </button>
                  <button
                    className={styles.closeBtn}
                    onClick={() => setClosingSymbol(pos.symbol)}
                    disabled={closeInProgress === pos.symbol}
                  >
                    {closeInProgress === pos.symbol ? 'Closing...' : 'Close'}
                  </button>
                </div>
              )}
            </div>
          ))}
          {spreads.map((spread) => (
            <div key={spread.key} className={styles.mobileCard}>
              <div className={styles.mobileCardHeader}>
                <span>
                  <span className={styles.spreadLabel}>SPREAD</span> <strong>{spread.underlying}</strong>
                </span>
                <span className={styles.spreadExpiry}>Exp {spread.expirationShort}</span>
              </div>
              <div className={styles.mobileCardBody}>
                <div className={styles.mobileCardRow}>
                  <span>P&L</span>
                  <span className={pnlClass(spread.totalPnl)}>${fmt(spread.totalPnl)}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span>Value</span>
                  <span>${fmt(spread.totalMarketValue)}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span>Legs</span>
                  <span>{spread.legs.length}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation modal — single position (stocks) */}
      {closingSymbol && closingPosition && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setClosingSymbol(null);
            setCloseQty(null);
          }}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Close Position</h3>
            {isLive && <span className={styles.liveBadge}>LIVE ACCOUNT</span>}
            {(() => {
              // Use absolute value so short positions (qty negative) compute
              // trim % and isFull correctly — we send a positive qty to Alpaca
              // regardless of side.
              const totalQty = Math.abs(parseFloat(closingPosition.qty || '0'));
              const isFull = closeQty == null || closeQty >= totalQty;
              const targetQty = isFull ? totalQty : closeQty!;
              const summary = isFull
                ? `entire ${totalQty} ${closingSymbol} position`
                : `${targetQty} of ${totalQty} ${closingSymbol}`;
              const setPercent = (pct: number) => {
                const q = pct >= 100 ? null : Math.max(1, Math.floor((totalQty * pct) / 100));
                setCloseQty(q);
              };
              return (
                <>
                  <p>
                    Close <strong>{summary}</strong> at market? This executes immediately.
                  </p>
                  <div className={styles.modalMeta}>
                    <span>Entry: ${fmt(closingPosition.avg_entry_price)}</span>
                    <span>Current: ${fmt(closingPosition.current_price)}</span>
                    <span className={pnlClass(closingPosition.unrealized_pl)}>
                      P&L: ${fmt(closingPosition.unrealized_pl)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      margin: '12px 0',
                    }}
                  >
                    <span style={{ fontSize: '0.85rem', opacity: 0.75 }}>Trim:</span>
                    {[25, 50, 75, 100].map((pct) => {
                      const expectedQty = pct >= 100 ? totalQty : Math.max(1, Math.floor((totalQty * pct) / 100));
                      const active = isFull ? pct === 100 : closeQty === expectedQty;
                      return (
                        <button
                          key={pct}
                          type="button"
                          onClick={() => setPercent(pct)}
                          style={{
                            padding: '4px 10px',
                            fontSize: '0.85rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            border: active ? '1px solid #3498db' : '1px solid rgba(255,255,255,0.2)',
                            background: active ? 'rgba(52, 152, 219, 0.18)' : 'transparent',
                            color: 'inherit',
                            fontWeight: active ? 700 : 500,
                          }}
                        >
                          {pct === 100 ? 'All' : `${pct}%`}
                        </button>
                      );
                    })}
                    <span style={{ marginLeft: '8px', fontSize: '0.85rem', opacity: 0.75 }}>or qty:</span>
                    <input
                      type="number"
                      min={1}
                      max={totalQty}
                      step={1}
                      value={closeQty ?? ''}
                      placeholder={String(totalQty)}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          setCloseQty(null);
                          return;
                        }
                        const n = Math.floor(Number(v));
                        if (Number.isFinite(n) && n > 0) {
                          setCloseQty(Math.min(n, totalQty));
                        }
                      }}
                      style={{
                        width: '90px',
                        padding: '4px 8px',
                        fontSize: '0.85rem',
                        borderRadius: '6px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'transparent',
                        color: 'inherit',
                      }}
                    />
                  </div>
                  <div className={styles.modalActions}>
                    <button
                      className={styles.cancelBtn}
                      onClick={() => {
                        setClosingSymbol(null);
                        setCloseQty(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className={styles.confirmCloseBtn}
                      onClick={handleConfirmClose}
                      disabled={closeInProgress !== null}
                    >
                      {closeInProgress
                        ? 'Closing...'
                        : isFull
                          ? 'Close All'
                          : `Close ${targetQty} ${targetQty === 1 ? 'share' : 'shares'}`}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Open Orders */}
      {orders.length > 0 && (
        <div className={styles.openOrders}>
          <h3 className={styles.sectionTitle}>Open Orders</h3>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Side</th>
                <th>Type</th>
                <th>Qty</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order: any) => (
                <tr key={order.id}>
                  <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{order.symbol}</td>
                  <td>{order.side?.toUpperCase()}</td>
                  <td>{order.type}</td>
                  <td>{order.qty}</td>
                  <td>{order.status}</td>
                  <td>{new Date(order.created_at).toLocaleString()}</td>
                  <td>
                    {isAdmin && (
                      <button
                        onClick={() => handleCancelOrder(order.id)}
                        disabled={
                          cancellingOrder === order.id || order.status === 'filled' || order.status === 'cancelled'
                        }
                        style={{ padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}
                      >
                        {cancellingOrder === order.id ? 'Cancelling...' : 'Cancel'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}>
          {toast.message}
        </div>
      )}
    </section>
  );
};

export default Positions;
